import React from 'react';

import { cssMap } from '@compiled/react';
import { CharacterCounterField } from '@atlaskit/form/character-counter-field';
import { useFormState } from '@atlaskit/form/use-form-state';
import type { CharacterCounterFieldProps } from '@atlaskit/form/character-counter-field';
import { token } from '@atlaskit/tokens';

type SupportedElements = HTMLInputElement | HTMLTextAreaElement;

interface SnippetCharacterCounterFieldProps<
	FieldValue = string,
	Element extends SupportedElements = HTMLInputElement,
> extends Omit<CharacterCounterFieldProps<FieldValue, Element>, 'underMaximumMessage'> {
	cutoff: number;
}

const styles = cssMap({
	fieldWrapper: {
		// Adds visual spacing between the field name and input
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'& label': {
			paddingBlockEnd: token('space.050'),
		},
		// Atlaskit Field adds an 8px top margin (`space.100`) for standalone fields.
		// These snippet fields are already spaced by the parent Stack, so remove only
		// the nested Field margin without changing the character counter spacing.
		// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
		'& > div > div': {
			marginBlockStart: token('space.0'),
		},
	},
});

/**
 * Wraps CharacterCounterField to only show the "X characters remaining"
 * message once the field value reaches the cutoff length.
 */
export function SnippetCharacterCounterField<
	FieldValue = string,
	Element extends SupportedElements = HTMLInputElement,
>({ cutoff, name, ...rest }: SnippetCharacterCounterFieldProps<FieldValue, Element>) {
	const formState = useFormState({ values: true });
	const fieldValue = String(formState?.values?.[name] ?? '');
	const underMaximumMessage = fieldValue.length >= cutoff ? undefined : '\u00A0';

	return (
		<div css={styles.fieldWrapper}>
			<CharacterCounterField<FieldValue, Element>
				name={name}
				underMaximumMessage={underMaximumMessage}
				{...rest}
			/>
		</div>
	);
}
